import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/store/authStore';
import { useShallow } from 'zustand/react/shallow';

interface NotificationPermission {
  granted: boolean;
  denied: boolean;
  default: boolean;
}

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  message: string | null;
  read: boolean;
  created_at: string | null;
}

// Som de notificação
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (error) {
    console.error('Error playing notification sound:', error);
  }
};

export function useNotifications() {
  const { user } = useAuthStore(useShallow((s) => ({ user: s.user })));
  const [permission, setPermission] = useState<NotificationPermission>({
    granted: false,
    denied: false,
    default: true,
  });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const notificationRef = useRef<Notification | null>(null);

  // Verificar permissão de notificações
  useEffect(() => {
    if ('Notification' in window) {
      setPermission({
        granted: Notification.permission === 'granted',
        denied: Notification.permission === 'denied',
        default: Notification.permission === 'default',
      });
    }
  }, []);

  // Solicitar permissão
  const requestPermission = async () => {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      setPermission({ granted: true, denied: false, default: false });
      return true;
    }

    if (Notification.permission !== 'denied') {
      const result = await Notification.requestPermission();
      const granted = result === 'granted';
      setPermission({
        granted,
        denied: result === 'denied',
        default: result === 'default',
      });
      return granted;
    }

    return false;
  };

  // Mostrar notificação
  const showNotification = async (
    title: string,
    options: NotificationOptions = {}
  ) => {
    if (!('Notification' in window)) {
      return;
    }

    // Solicitar permissão se necessário
    if (Notification.permission === 'default') {
      const granted = await requestPermission();
      if (!granted) return;
    }

    if (Notification.permission === 'granted') {
      // Fechar notificação anterior se existir
      if (notificationRef.current) {
        notificationRef.current.close();
      }

      const notification = new Notification(title, {
        icon: '/src/assets/logos/squared/innflow_favicon_32x32.png',
        badge: '/src/assets/logos/squared/innflow_favicon_32x32.png',
        tag: 'support-message',
        requireInteraction: false,
        ...options,
      });

      notificationRef.current = notification;

      // Tocar som se habilitado
      if (soundEnabled) {
        playNotificationSound();
      }

      // Fechar automaticamente após 5 segundos
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Ao clicar, focar na janela
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      return notification;
    }
  };

  // Toggle som
  const toggleSound = () => {
    setSoundEnabled((prev) => !prev);
    // Salvar preferência
    localStorage.setItem('notificationSoundEnabled', String(!soundEnabled));
  };

  // Carregar preferência de som
  useEffect(() => {
    const saved = localStorage.getItem('notificationSoundEnabled');
    if (saved !== null) {
      setSoundEnabled(saved === 'true');
    }
  }, []);

  // Buscar alertas (notificações in-app) do Supabase
  const fetchAlerts = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }
    const { data, error } = await supabase
      .from('alerts')
      .select('id, type, title, message, is_read, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) {
      setNotifications([]);
      return;
    }
    setNotifications(
      (data ?? []).map((row) => ({
        id: row.id,
        type: row.type ?? 'info',
        title: row.title ?? '',
        message: row.message ?? null,
        read: !!row.is_read,
        created_at: row.created_at,
      }))
    );
  }, [user?.id]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel('alerts-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'alerts', filter: `user_id=eq.${user.id}` }, fetchAlerts)
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchAlerts]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = useCallback(
    async (id: string) => {
      if (!user?.id) return;
      await (supabase as any).from('alerts').update({ is_read: true }).eq('id', id).eq('user_id', user.id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    },
    [user?.id]
  );

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;
    await (supabase as any).from('alerts').update({ is_read: true }).eq('user_id', user.id);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, [user?.id]);

  const deleteNotification = useCallback(
    async (id: string) => {
      if (!user?.id) return;
      await (supabase as any).from('alerts').delete().eq('id', id).eq('user_id', user.id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    },
    [user?.id]
  );

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    permission,
    soundEnabled,
    requestPermission,
    showNotification,
    toggleSound,
    playSound: playNotificationSound,
  };
}
